import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp, Database,
  Building2, Radio, Activity, Eye,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const SwitzerlandPage = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('features');
  const [openAccordion, setOpenAccordion] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const images = [
    {
      url: "/images/locations/unsplash_1574904935745-7e40279f589d.jpg",
      alt: "Geneva Jet d'Eau Fountain"
    },
    {
      url: "/images/locations/unsplash_1521292270410-a8c4d716d518.jpg",
      alt: "Swiss Alps Panorama"
    },
    {
      url: "/images/locations/unsplash_1527668752968-14dc70a27c95.jpg",
      alt: "Swiss Alpine Village"
    },
    {
      url: "/images/locations/unsplash_1593186344142-ef775a6e596f.jpg",
      alt: "Swiss Mountain Railway"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { icon: Clock, value: t('switzerland.stats.latency'), label: t('switzerland.stats.latencyLabel') },
    { icon: Zap, value: t('switzerland.stats.speed'), label: t('switzerland.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('switzerland.stats.uptime'), label: t('switzerland.stats.uptimeLabel') },
    { icon: MapPin, value: t('switzerland.stats.location'), label: t('switzerland.stats.locationLabel') }
  ];

  const tabs = [
    { id: 'features', label: t('switzerland.tabs.features'), icon: Server },
    { id: 'security', label: t('switzerland.tabs.security'), icon: Shield },
    { id: 'performance', label: t('switzerland.tabs.performance'), icon: Zap },
    { id: 'support', label: t('switzerland.tabs.support'), icon: Activity }
  ];

  const accordionItems = [
    {
      title: t('switzerland.infrastructure.item1.title'),
      content: t('switzerland.infrastructure.item1.content'),
      icon: Building2
    },
    {
      title: t('switzerland.infrastructure.item2.title'),
      content: t('switzerland.infrastructure.item2.content'),
      icon: Radio
    },
    {
      title: t('switzerland.infrastructure.item3.title'),
      content: t('switzerland.infrastructure.item3.content'),
      icon: Shield
    },
    {
      title: t('switzerland.infrastructure.item4.title'),
      content: t('switzerland.infrastructure.item4.content'),
      icon: Activity
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="switzerland" />
      <Header />
      
      {/* Split Screen Hero - Swiss Red Theme */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-red-900 via-red-800 to-red-900 py-20 sm:py-24 md:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-700/20 border border-red-600/30 rounded-full text-red-50 text-sm font-semibold mb-6">
                <MapPin className="w-4 h-4" />
                <span>{t('switzerland.hero.badge')}</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
                {t('switzerland.hero.title')}
              </h1>
              <p className="text-2xl text-red-50 mb-8 leading-relaxed">
                {t('switzerland.hero.subtitle')}
              </p>
              
              {/* Stats in Hero */}
              <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
                {stats.slice(0, 2).map((stat, index) => {
                  const IconComponent = stat.icon;
                  return (
                    <div key={index} className="bg-white/10 backdrop-blur-sm p-3 sm:p-4 rounded-lg border border-white/20">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-red-200" />
                        <div>
                          <div className="text-2xl sm:text-3xl font-bold">{stat.value}</div>
                          <div className="text-xs sm:text-sm text-red-200">{stat.label}</div>
                          {stat.wiki && (
                            <Link
                              to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                              className="mt-2 inline-flex items-center gap-1 text-xs text-blue-300 hover:text-white transition-colors"
                            >
                              {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            
              <div className="flex gap-4">
                <Button 
                  onClick={scrollToPricing}
                  className="bg-white text-red-900 hover:bg-red-50 font-semibold px-8 py-6 text-lg"
                >
                  {t('switzerland.hero.getStarted')}
                </Button>
                <Button 
                  onClick={scrollToPricing}
                  className="bg-red-950 text-white hover:bg-black font-semibold px-8 py-6 text-lg border-2 border-white/20"
                >
                  {t('switzerland.hero.viewPlans')}
                </Button>
              </div>
            </div>
            
            {/* Right Side - Image Carousel */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-[600px]">
                {images.map((image, index) => (
                  <img key={index}
                    src={image.url} 
                    alt={image.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      currentImage === index ? 'opacity-100' : 'opacity-0'
                    }`}
 loading="lazy"/>
                ))}
              </div>
              
              {/* Image Indicators */}
              <div className="flex gap-2 justify-center mt-4">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      currentImage === index 
                        ? 'bg-white w-8' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section - Unique Layout */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('switzerland.tabs.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('switzerland.tabs.subtitle')}
            </p>
          </div>

          {/* Tabs Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeTab === tab.id
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {activeTab === 'features' && (
              <div className="grid md:grid-cols-3 gap-6">
                {t('switzerland.features.items').map((item, index) => (
                  <div key={index} className="p-6 bg-gradient-to-br from-red-50 to-white rounded-xl border border-red-100">
                    <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-4">
                      <HardDrive className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            )}
            
            {activeTab === 'security' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('switzerland.security.title')}</h3>
                  <p className="text-gray-600 mb-6">{t('switzerland.security.description')}</p>
                  <ul className="space-y-3">
                    {t('switzerland.security.items').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img src="/images/locations/unsplash_1690715942718-879e206f4d6b.jpg" 
                    alt="Swiss Security" loading="lazy"
                    className="w-full h-full object-cover"
 loading="lazy"/>
                </div>
              </div>
            )}
            
            {activeTab === 'performance' && (
              <div className="text-center">
                <Zap className="w-16 h-16 text-red-600 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('switzerland.performance.title')}</h3>
                <p className="text-gray-600 max-w-2xl mx-auto mb-8">{t('switzerland.performance.description')}</p>
                <div className="grid md:grid-cols-4 gap-6">
                  {t('switzerland.performance.metrics').map((metric, index) => (
                    <div key={index} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-xl">
                      <div className="text-4xl font-bold text-red-600 mb-2">{metric.value}</div>
                      <div className="text-sm text-gray-600">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'support' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div className="rounded-xl overflow-hidden">
                  <img src="/images/locations/unsplash_1667758682790-c58fa23dc76f.jpg" 
                    alt="Swiss Support" loading="lazy"
                    className="w-full h-full object-cover"
 loading="lazy"/>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('switzerland.support.title')}</h3>
                  <p className="text-gray-600 mb-6">{t('switzerland.support.description')}</p>
                  <div className="space-y-4">
                    {t('switzerland.support.channels').map((channel, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                        <Activity className="w-6 h-6 text-red-600 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-900">{channel.name}</div>
                          <div className="text-sm text-gray-600">{channel.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related VPS Locations */}
      <RelatedLocations currentLocation="vps-switzerland" />

      {/* Pricing - High Position */}
      <div id="pricing">
        <PricingSection 
          hideHeader={false}
          title={t('switzerland.pricing.title')}
          subtitle={t('switzerland.pricing.subtitle')}
        />
      </div>

      <PaymentMethodsSection transparentBg={false} />

      {/* Accordion Infrastructure Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('switzerland.infrastructure.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('switzerland.infrastructure.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {accordionItems.map((item, index) => {
              const IconComponent = item.icon;
              const isOpen = openAccordion === index;
              
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-red-100"
                >
                  <button
                    onClick={() => setOpenAccordion(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-red-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className="text-xl font-bold text-gray-900">{item.title}</span>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="w-6 h-6 text-red-600" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-gray-400" />
                    )}
                  </button>
                  
                  {isOpen && (
                    <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                      {item.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Asymmetric Location Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Large image on left */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-96">
                <img src="/images/locations/unsplash_1690715942549-0cb0d3c3e808.jpg" 
                  alt="Geneva Architecture" loading="lazy"
                  className="w-full h-full object-cover"
 loading="lazy"/>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg h-48">
                  <img src="/images/locations/unsplash_1574904935745-7e40279f589d.jpg" 
                    alt="Geneva Jet d'Eau" loading="lazy"
                    className="w-full h-full object-cover"
 loading="lazy"/>
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg h-48">
                  <img src="/images/locations/320px-Flag_of_Switzerland.svg.png" 
                    alt="Switzerland Flag" loading="lazy"
                    className="w-full h-full object-cover"
 loading="lazy"/>
                </div>
              </div>
            </div>
            
            {/* Content on right */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('switzerland.location.title')}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('switzerland.location.description')}
              </p>
              <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-600" />
                  {t('switzerland.location.subtitle')}
                </h3>
                <p className="text-gray-700 text-sm">{t('switzerland.location.details')}</p>
              </div>
              <ul className="space-y-3">
                {t('switzerland.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA with unique design */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-red-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-white/10 backdrop-blur-md border-2 border-white/20 rounded-2xl p-12">
            <h2 className="text-5xl font-bold text-white mb-6">
              {t('switzerland.cta.title')}
            </h2>
            <p className="text-2xl text-red-100 mb-8">
              {t('switzerland.cta.subtitle')}
            </p>
            <Button 
              onClick={scrollToPricing}
              className="bg-white text-red-900 hover:bg-red-50 font-semibold px-12 py-8 text-xl"
            >
              {t('switzerland.cta.button')}
            </Button>
          </div>
        </div>
      </section>

      <LocationFAQ location="vps-switzerland" />
      <Footer />
    </div>
  );
};


